import React from 'react';
import { useNavigate } from 'react-router-dom';

const SITE = 'theoperatormethod.com';
const SUPPORT_EMAIL = 'support@theoperatormethod.com';
const COMMENTS_EMAIL = 'comments@theoperatormethod.com';

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div style={s.page}>
      {/* Nav */}
      <div style={s.nav}>
        <button onClick={() => navigate('/')} style={s.logoBtn}>THE OPERATOR METHOD</button>
        <button onClick={() => navigate(-1)} style={s.backBtn}>&larr; Back</button>
      </div>

      <div style={s.content}>
        <p style={s.eyebrow}>About</p>
        <h1 style={s.headline}>The founder of TheOperatorMethod is still a student of it.</h1>
        <p style={s.subhead}>There is no end to learning.</p>

        {/* ── Origin Story ── */}
        <Section title="Who Built This">
          <p>A retired individual who woke up at 4am for four months straight and built a certified AI literacy training platform &mdash; with no formal coding background &mdash; using AI tools as implementation partners and the skills this platform teaches as the primary development method.</p>
          <p>The platform itself was conceived and repeatedly rebuilt during early morning burst sessions &mdash; threshold hours between sleep and full wakefulness where creative thinking flows without resistance. This was not incidental. It became a named teachable method inside the curriculum itself. The founder&rsquo;s process became the course content.</p>
        </Section>

        {/* ── Why It Exists ── */}
        <Section title="Why It Exists">
          <p>The bottleneck in Artificial Intelligence is not the AI. It is the human-to-AI interface. Most people have access to extraordinarily powerful AI tools and no reliable way to direct them effectively.</p>
          <p>TheOperatorMethod exists to close that gap &mdash; through direct supervised practice, performance-based progression, and real certification of achievement.</p>
        </Section>

        {/* ── The Recursive Truth ── */}
        <Section title="The Recursive Truth">
          <p>This platform was built using the exact skills it teaches. Every late night burst session, every AI-assisted rebuild, every moment of friction and breakthrough &mdash; was itself a demonstration of the Operator Method in action. The founder was the first student. The platform is the proof.</p>
        </Section>

        {/* ── Philosophy ── */}
        <Section title="The Philosophy">
          <p>There is no arrived destination in learning &mdash; especially in a field moving as fast as AI. The founder is still a student of this material. That is not a disclaimer. That is the point.</p>
          <p>TheOperatorMethod is not a hall of credentials looked down upon from above &mdash; it is a community of practice, climbing together, with the most experienced climber turned around on the path saying &ldquo;here is what I have learned so far &mdash; come with me.&rdquo;</p>
          <p>People do not abandon learning because the subject is hard. They abandon it because they feel alone in the difficulty. TheOperatorMethod removes the pedestal. The bar is not perfection. Beginners are not just tolerated here &mdash; they are the entire reason this exists.</p>
        </Section>

        {/* ── Course Tiers ── */}
        <Section title="The Courses">
          <div style={s.tierCard}>
            <h4 style={s.tierName}>Full Operator &mdash; $97</h4>
            <p>The course. For professionals who use AI and get results in familiar areas &mdash; but hit a ceiling when work gets complex or multi-step. You will learn task decomposition, context-passing between steps, repeatable workflow design, and how to steer AI when it drifts mid-task. The assessment determines whether you
squo;re ready to start here directly.</p>
          </div>
          <div style={s.tierCard}>
            <h4 style={s.tierName}>Foundations &mdash; $39</h4>
            <p>The prep course for Full Operator. If the assessment identified gaps in the fundamentals Operator builds on &mdash; how AI processes instructions, what makes a brief complete, how to build a repeatable system &mdash; Foundations closes those gaps first. It exists so that when you step into Operator, nothing slows you down. Recommended only when the assessment says you need it.</p>
          </div>
          <div style={s.tierCard}>
            <h4 style={s.tierName}>Certified Operator &mdash; $149</h4>
            <p>For professionals ready to operate at the architecture level. You will learn how AI models actually reason, where they systematically fail, how to architect multi-model workflows, and how to lead others in AI use. Includes a certification credential upon demonstrated mastery.</p>
          </div>
        </Section>

        {/* ── How It Works ── */}
        <Section title="How the Training Works">
          <p>Every course uses the <strong>See/Do/Teach</strong> instruction method. For each concept, the instructor demonstrates it directly (See), you practice a parallel task (Do), then you explain the principle back in your own words (Teach). No guesswork. No discovery-by-confusion. Direct instruction, supervised practice, independent performance.</p>
          <p>Sessions are self-paced, one-on-one with an AI instructor. Your progress saves automatically. You have four weeks to complete each course, and you can log in and out as many times as you like. Every session picks up exactly where you left off.</p>
        </Section>

        {/* ── Technical / Support ── */}
        <Section title="Technical Support">
          <p>If you experience any technical issues &mdash; login problems, payment questions, session errors, or anything that prevents you from accessing your course &mdash; reach out directly:</p>
          <p style={s.emailLine}><a href={`mailto:${SUPPORT_EMAIL}`} style={s.link}>{SUPPORT_EMAIL}</a></p>
          <p>We aim to respond within 24 hours on business days. Please include your account email and a brief description of the issue.</p>
        </Section>

        {/* ── Comments / Feedback ── */}
        <Section title="Comments &amp; Feedback">
          <p>We read every message. If you have suggestions, feedback on the course content, or ideas for improvement:</p>
          <p style={s.emailLine}><a href={`mailto:${COMMENTS_EMAIL}`} style={s.link}>{COMMENTS_EMAIL}</a></p>
          <p>This platform was built by someone who is still learning. Your perspective makes it better.</p>
        </Section>

      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <h3 style={s.sectionTitle}>{title}</h3>
      <div style={s.sectionBody}>{children}</div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0a0e1a',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#f0f4ff',
  },
  nav: {
    padding: '20px 40px',
    borderBottom: '1px solid #1e2a3a',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logoBtn: {
    background: 'none',
    border: 'none',
    color: '#4f8ef7',
    cursor: 'pointer',
    fontSize: '11px',
    letterSpacing: '0.25em',
    fontWeight: 700,
  },
  backBtn: {
    background: 'none',
    border: '1px solid #1e2a3a',
    color: '#6b7a99',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '6px 14px',
    borderRadius: '6px',
  },
  content: {
    maxWidth: '760px',
    margin: '0 auto',
    padding: '60px 24px 80px',
  },
  eyebrow: {
    fontSize: '11px',
    letterSpacing: '0.25em',
    color: '#4f8ef7',
    textTransform: 'uppercase',
    marginBottom: '16px',
  },
  headline: {
    fontSize: '28px',
    fontWeight: 700,
    margin: '0 0 8px',
    color: '#f0f4ff',
    lineHeight: 1.3,
  },
  subhead: {
    fontSize: '16px',
    color: '#7a8ba8',
    fontStyle: 'italic',
    marginBottom: '48px',
  },
  section: {
    marginBottom: '40px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#c8d6e8',
    margin: '0 0 12px',
    borderBottom: '1px solid #1a2333',
    paddingBottom: '8px',
  },
  sectionBody: {
    fontSize: '15px',
    lineHeight: 1.75,
    color: '#a3b3cc',
  },
  tierCard: {
    background: 'rgba(79,142,247,0.06)',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '20px 24px',
    marginBottom: '16px',
  },
  tierName: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#4f8ef7',
    margin: '0 0 8px',
  },
  emailLine: {
    margin: '8px 0',
  },
  link: {
    color: '#4f8ef7',
    textDecoration: 'none',
  },
  footer: {
    marginTop: '60px',
    paddingTop: '24px',
    borderTop: '1px solid #1a2333',
    textAlign: 'center',
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  footerLink: {
    background: 'none',
    border: 'none',
    color: '#4f8ef7',
    cursor: 'pointer',
    fontSize: '13px',
  },
  footerAnchor: {
    color: '#4f8ef7',
    textDecoration: 'none',
    fontSize: '13px',
  },
  dot: {
    color: '#3a4560',
  },
  copyright: {
    fontSize: '12px',
    color: '#3a4560',
  },
};
